import { fn, concat, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { queue } from '@nullvoxpopuli/ember-composable-helpers';
import can from 'ember-can/helpers/can';
import type AbilitiesService from 'ember-can/services/abilities';
import { or } from 'ember-truth-helpers';
import type MetricsService from 'garaje/services/metrics';
import type PlanFeaturesService from 'garaje/services/plan-features';
import type StateService from 'garaje/services/state';
import type TrialService from 'garaje/services/trial';
import type CtaEventProperties from 'garaje/types/cta-event-properties';
import { alias, equal } from 'macro-decorators';

export interface UpgradeQueryParams extends Record<string, unknown> {
  plan: 'standard' | 'premium';
  period: 'monthly' | 'yearly';
}

interface FeatureOrUpgradeButtonSignature {
  Args: {
    featureName: string;
    isAuthorized: boolean;
    isSlack: boolean;
    level: string;
    logCTAEvents: boolean;
    onUpgrade?: () => void | null;
    shouldCheckMinLevel: boolean;
    upgradeButtonText?: string;
    upgradeClass?: string;
    upgradeQueryParams: UpgradeQueryParams;
    vrOnly: boolean;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLDivElement;
}

export default class FeatureOrUpgradeButton extends Component<FeatureOrUpgradeButtonSignature> {
  @service declare abilities: AbilitiesService;
  @service declare metrics: MetricsService;
  @service declare planFeatures: PlanFeaturesService;
  @service declare state: StateService;
  @service declare trial: TrialService;

  @alias('state.vrSubscription') declare vrSubscription: StateService['vrSubscription'];
  @alias('vrSubscription.hasTrialDaysLeft') declare hasTrialDaysLeft: boolean;
  @alias('trial.trials') declare plans: TrialService['trials'];
  @equal('vrSubscription.plan', 'basic') declare isBasicUser: boolean;

  get levelString(): string {
    const advertisedLevel = this.planFeatures.calcAdvertisedPlanId(this.args.level);
    return this.planFeatures.planIdToName(advertisedLevel);
  }

  get levelCapitalize(): string {
    return capitalize(this.levelString || '');
  }

  get showSignUpForVR(): boolean {
    const vrActive = isPresent(this.vrSubscription);

    return !vrActive && this.args.vrOnly && !this.args.isSlack;
  }

  get canAccessFeature(): boolean {
    const trial = this.trial;
    const { canTrialFeature } = trial;
    const isAuthorized = this.args.isAuthorized;
    const level = this.args.level;
    const shouldCheckMinLevel = this.args.shouldCheckMinLevel;
    const hasDeliveries = !!this.state?.features?.canAccessDeliveriesApplication;

    if (isAuthorized) {
      return true;
    }
    if (this.args.isSlack && hasDeliveries) {
      return true;
    }
    if (canTrialFeature) {
      return trial.canAccess(level);
    }
    if (shouldCheckMinLevel) {
      return this._hasMinLevel(level);
    }
    return false;
  }

  get upgradeButtonText(): string {
    if (this.args.upgradeButtonText) return this.args.upgradeButtonText;
    if (this.levelCapitalize) return `Upgrade to ${this.levelCapitalize}`;
    return `Upgrade`;
  }

  _hasMinLevel(level: string): boolean {
    const actualPlan = this.plans.indexOf(this.vrSubscription?.plan ?? '');
    const requiredPlan = this.plans.indexOf(level);
    return actualPlan >= requiredPlan;
  }

  @action
  upgrade(): void {
    if (typeof this.args.onUpgrade === 'function') {
      this.args.onUpgrade();
    }
  }

  ctaEventProperties(intent: string, text: string): CtaEventProperties {
    return {
      cta_id: this.args.featureName
        ? `${this.args.featureName}_feature_or_upgrade_button`
        : 'feature_or_upgrade_button',
      cta_title: null,
      cta_body: null,
      cta_type: 'button',
      cta_clickable_type: this.abilities.can('visit billing') ? 'button' : 'none',
      cta_clickable_text: this.abilities.can('visit billing') ? text : null,
      cta_intent: intent,
    };
  }

  get shouldLogCTAEvents(): boolean {
    // default behavior is to log, unless @logCTAEvents is explicitly false (not just falsy)
    return this.args.logCTAEvents !== false;
  }

  @action
  logCTAViewedEvent(intent: string, element: HTMLElement): void {
    if (this.shouldLogCTAEvents) {
      this.metrics.trackEvent('CTA Viewed', this.ctaEventProperties(intent, element.innerText.trim()));
    }
  }

  @action
  logCTAClickedEvent(intent: string, event: Event): void {
    if (this.shouldLogCTAEvents) {
      this.metrics.trackEvent(
        'CTA Clicked',
        this.ctaEventProperties(
          intent,
          (event.target as null | (EventTarget & { innerText: string }))!.innerText.trim(),
        ),
      );
    }
  }

  <template>
    <div class="inline-block" data-feature-or-upgrade-button-wrapper ...attributes>
      {{#if this.showSignUpForVR}}
        <LinkTo
          data-test-vr-sign-up="true"
          @route="visitors.start-trial"
          {{didInsert (fn this.logCTAViewedEvent "trial")}}
          {{on "click" (fn this.logCTAClickedEvent "trial")}}
          class={{concat "btn btn--small btn--brand my-4 " @upgradeClass}}
        >
          Try with Visitors
        </LinkTo>
      {{else if this.canAccessFeature}}
        {{yield}}
      {{else if (can "visit billing")}}
        {{#if this.trial.canTrialFeature}}
          {{! Change trial button }}
          <LinkTo
            @route="billing"
            @query={{hash showPlan=this.levelString}}
            {{didInsert (fn this.logCTAViewedEvent "trial")}}
            {{on "click" (fn this.logCTAClickedEvent "trial")}}
            class="btn btn--small btn--brand {{@upgradeClass}}"
            data-test-trial-link
          >
            {{or @upgradeButtonText (concat "Try " this.levelCapitalize)}}
          </LinkTo>
        {{else}}
          {{! Upgrade plan button }}
          {{#if @upgradeQueryParams}}
            <LinkTo
              data-test-upgrade-link
              data-test-upgrade-billing-subscribe
              @route="billing.subscribe"
              @query={{@upgradeQueryParams}}
              {{on "click" (queue (fn this.logCTAClickedEvent "upgrade") this.upgrade)}}
              {{didInsert (fn this.logCTAViewedEvent "upgrade")}}
              class="btn btn--small btn--brand {{@upgradeClass}}"
            >
              {{this.upgradeButtonText}}
            </LinkTo>
          {{else}}
            <LinkTo
              data-test-upgrade-link
              data-test-upgrade-billing-plans
              @route="billing.plans"
              {{on "click" (queue (fn this.logCTAClickedEvent "upgrade") this.upgrade)}}
              {{didInsert (fn this.logCTAViewedEvent "upgrade")}}
              class="btn btn--small btn--brand {{@upgradeClass}}"
            >
              {{this.upgradeButtonText}}
            </LinkTo>
          {{/if}}
        {{/if}}
      {{else}}
        <div
          class="text-sm text-carbon-50"
          data-test-upgrade-message
          {{didInsert (fn this.logCTAViewedEvent "upgrade")}}
        >
          Upgrade required. Contact your global admin.
        </div>
      {{/if}}
    </div>
  </template>
}
